/**
 * capacitorShim.js — Abstraction Capacitor / Web
 *
 * APPROCHE : Capacitor injecte window.Capacitor dans le WebView natif.
 * On accède aux plugins via window.Capacitor.Plugins sans aucun import npm.
 * → Le build React fonctionne sans Capacitor installé.
 * → En natif iOS/Android, les plugins sont disponibles via window.Capacitor.
 *
 * Pour installer Capacitor (depuis ton terminal sur ton Mac/PC) :
 *   npm install @capacitor/core @capacitor/cli @capacitor/camera \
 *               @capacitor/network @capacitor/app @capacitor/status-bar \
 *               @capacitor/splash-screen
 *   npm run mobile:setup
 */

// ── Accès sécurisé à Capacitor ───────────────────────────────────────────────
const cap      = () => window.Capacitor;
const plugins  = () => window.Capacitor?.Plugins || {};

// ── Détection de plateforme ───────────────────────────────────────────────────
function detectPlatform() {
  const capacitor = cap();
  if (capacitor?.isNativePlatform?.()) {
    return {
      isNative:  true,
      platform:  capacitor.getPlatform?.() || 'web',
      isMobile:  true,
    };
  }

  const ua            = navigator.userAgent || '';
  const isIOS_UA      = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid_UA  = /Android/i.test(ua);
  const isMobileUA    = isIOS_UA || isAndroid_UA;
  const isTouchOnly   = navigator.maxTouchPoints > 2 && !window.matchMedia('(pointer:fine)').matches;
  const isSmallScreen = window.innerWidth <= 768;

  return {
    isNative:  false,
    platform:  isIOS_UA ? 'ios' : isAndroid_UA ? 'android' : 'web',
    isMobile:  isMobileUA || (isTouchOnly && isSmallScreen),
  };
}

export const PLATFORM   = detectPlatform();
export const IS_NATIVE  = PLATFORM.isNative;
export const IS_MOBILE  = IS_NATIVE || PLATFORM.isMobile;
export const IS_IOS     = PLATFORM.platform === 'ios';
export const IS_ANDROID = PLATFORM.platform === 'android';

// ── Caméra ────────────────────────────────────────────────────────────────────
/**
 * Prend une photo ou ouvre la galerie.
 * @param {'CAMERA'|'PHOTOS'} source
 * @returns {Promise<string>} dataURL base64
 */
export async function takePhoto(source = 'CAMERA') {
  const Camera = plugins().Camera;

  if (IS_NATIVE && Camera) {
    try {
      const photo = await Camera.getPhoto({
        quality:      85,
        allowEditing: false,
        resultType:   'dataUrl',        // CameraResultType.DataUrl
        source:       source === 'CAMERA' ? 'CAMERA' : 'PHOTOS',
      });
      return photo.dataUrl;
    } catch (e) {
      console.warn('[capacitorShim] Camera error:', e?.message);
    }
  }

  // Fallback web : input type=file
  return new Promise((resolve, reject) => {
    const input    = document.createElement('input');
    input.type     = 'file';
    input.accept   = 'image/*';
    if (source === 'CAMERA') input.capture = 'environment';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) { reject(new Error('Aucun fichier sélectionné')); return; }
      const reader  = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

// ── Réseau ────────────────────────────────────────────────────────────────────
export async function getNetworkStatus() {
  const Network = plugins().Network;
  if (IS_NATIVE && Network) {
    try { return await Network.getStatus(); } catch (_) {}
  }
  return { connected: navigator.onLine, type: navigator.onLine ? 'wifi' : 'none' };
}

export function onNetworkChange(cb) {
  const Network = plugins().Network;
  if (IS_NATIVE && Network) {
    let handle = null;
    Network.addListener('networkStatusChange', cb)
      .then((h) => { handle = h; })
      .catch(() => {});
    return () => handle?.remove?.();
  }
  const onOnline  = () => cb({ connected: true,  type: 'wifi' });
  const onOffline = () => cb({ connected: false, type: 'none' });
  window.addEventListener('online',  onOnline);
  window.addEventListener('offline', onOffline);
  return () => {
    window.removeEventListener('online',  onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

// ── Application ───────────────────────────────────────────────────────────────
export function onBackButton(cb) {
  const App = plugins().App;
  if (!IS_NATIVE || !App) return () => {};
  let handle = null;
  App.addListener('backButton', cb).then((h) => { handle = h; }).catch(() => {});
  return () => handle?.remove?.();
}

// ── StatusBar ─────────────────────────────────────────────────────────────────
export async function setStatusBarDark() {
  const StatusBar = plugins().StatusBar;
  if (!IS_NATIVE || !StatusBar) return;
  try {
    await StatusBar.setStyle({ style: 'DARK' });
    await StatusBar.setBackgroundColor({ color: '#1C1915' });
  } catch (_) {}
}

// ── SplashScreen ──────────────────────────────────────────────────────────────
export async function hideSplash() {
  const SplashScreen = plugins().SplashScreen;
  if (!IS_NATIVE || !SplashScreen) return;
  try { await SplashScreen.hide(); } catch (_) {}
}
